import { http } from "../../shared/api/http.js";

interface InitResponse {
  mediaItemId: string;
  strategy: "single" | "multipart";
  uploadId?: string;
  urls: { partNumber: number; url: string }[];
  partSize: number;
}

/** Upload direct navigateur → MinIO (PUT présigné, multipart résumable si gros fichier). */
export async function uploadMedia(opts: {
  eventId: string;
  token: string;
  file: Blob;
  kind: "image" | "video";
  contentType: string;
  name: string;
  email: string;
  note?: string;
  onProgress?: (pct: number) => void;
}): Promise<string> {
  const t = encodeURIComponent(opts.token);
  const init = await http.post<InitResponse>(`/events/${opts.eventId}/media?t=${t}`, {
    kind: opts.kind,
    contentType: opts.contentType,
    bytes: opts.file.size,
    name: opts.name,
    email: opts.email,
    note: opts.note ?? "",
  });

  if (init.strategy === "single") {
    await putPart(init.urls[0]!.url, opts.file, opts.contentType);
    opts.onProgress?.(100);
    await http.post(`/media/${init.mediaItemId}/complete?t=${t}`, {});
    return init.mediaItemId;
  }

  const parts: { partNumber: number; etag: string }[] = [];
  for (const [i, { partNumber, url }] of init.urls.entries()) {
    const start = (partNumber - 1) * init.partSize;
    const chunk = opts.file.slice(start, start + init.partSize);
    parts.push({ partNumber, etag: await putPartWithRetry(url, chunk, opts.contentType) });
    opts.onProgress?.(Math.round(((i + 1) / init.urls.length) * 100));
  }
  await http.post(`/media/${init.mediaItemId}/complete?t=${t}`, { parts });
  return init.mediaItemId;
}

async function putPart(url: string, body: Blob, contentType: string): Promise<string> {
  const res = await fetch(url, { method: "PUT", body, headers: { "Content-Type": contentType } });
  if (!res.ok) throw new Error(`PUT ${res.status}`);
  return (res.headers.get("etag") ?? "").replace(/"/g, "");
}

async function putPartWithRetry(url: string, body: Blob, ct: string, tries = 3): Promise<string> {
  let lastErr: unknown;
  for (let i = 0; i < tries; i++) {
    try {
      return await putPart(url, body, ct);
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
    }
  }
  throw lastErr;
}
